import Entity, { Department, EntityProfile } from "@/entities/entity";
import { AsyncPaginatedRequest } from "./";
import { LazyAsyncIter } from "@/iterators";
import HttpService from "./http-service";
import { pageMapperBuilder, Paginated } from "@/pagination";
import { SnakeCasedProperties } from "type-fest";
import LocalPaginated from "@/pagination/paginated";
import { AxiosResponse } from "axios";
import { User } from "@/entities";
//import { ProfilePicture } from "@/entities/user";
import { UserDTO, userFromDTO } from "./user/dto";

/**
 * Queries data of entities from the API.
 */
export default class EntityService extends HttpService {
  public getEntities(): AsyncIterator<Paginated<Entity>> {
    return this.searchEntitiesAsync();
  }

  public searchEntitiesAsync(
    params: SearchEntitiesParams = {}
  ): LazyAsyncIter<Paginated<Entity>> {
    const reqIter = new AsyncPaginatedRequest<EntityDTO>(
      "get",
      "entities",
      params,
      undefined,
      this.httpClient
    )[Symbol.asyncIterator]();

    return new LazyAsyncIter(reqIter).map((page) => ({
      ...page,
      pageElements: page.pageElements?.map(fromEntityDTO),
    }));
  }

  public async getEntity(id: number, with_user?: boolean): Promise<Entity> {
    const entityDTOResponse = await this.httpClient.get<EntityDTO>(
      `entities/${id}`,
      { params: { with_user } }
    );
    return fromEntityDTO(entityDTOResponse.data);
  }

  public async createEntity(params: PartialEntity): Promise<void> {
    const partial = fromPartialEntity(params);
    return this.httpClient.post("entities", partial);
  }

  public getEntityAdminUsers(
    entity_id: number
  ): LazyAsyncIter<Paginated<User>> {
    const params = {
      entity_id,
    };
    const reqIter = new AsyncPaginatedRequest<UserDTO>(
      "get",
      "entities-admin-users",
      params,
      undefined,
      this.httpClient
    )[Symbol.asyncIterator]();

    return new LazyAsyncIter(reqIter).map((page) => ({
      ...page,
      pageElements: page.pageElements?.map(userFromDTO),
    }));
  }

  public async updateEntity(params: Entity): Promise<void> {
    const partial = fromPartialEntity(params);
    return this.httpClient.patch(`entities/${params.id}`, partial);
  }

  public async changeStatusEntity(entity:Entity):Promise<void>
  {

    return this.httpClient.post(`entities/${entity.id}/status`);
  }

  public getEntityDepartments(
    entityId: number,
    status: 'all' | 'active' | 'inactive' = 'all'
  ): AsyncIterator<LocalPaginated<Department>> {
    console.log(status)
    const query = {status}
    const reqIter = new AsyncPaginatedRequest<DepartmentDTO>(
      "get",
      `entities/${entityId}/departments`,
      query,
      undefined,
      this.httpClient
    )[Symbol.asyncIterator]();
    return new LazyAsyncIter(reqIter).map(pageMapperBuilder(fromDepartmentDTO));
  }

  public async createDepartment(params: DepartmentCreate): Promise<number> {
    const { data } = await this.httpClient.post<number>(
      `entities/${params.entityId}/departments`,
      params
    );
    return data;
  }
  public async updateDepartment(department: Department): Promise<void> {
    const params = {
      name: department.name,
      acronym: department.acronym,
      index: department.index,
      status: department.status
    };
    return this.httpClient.patch(`departments/${department.id}`, params);
  }

  public async deleteDepartment(department: Department):Promise<void>
  {
    return this.httpClient.delete(`departments/${department.id}`)
  }

  public async changeStatusDepartment(department:Department):Promise<void>
  {

    return this.httpClient.post(`department/${department.id}/status`);
  }

  public async deleteEntity(entityId: number): Promise<AxiosResponse<void>> {
    return this.httpClient.delete(`entities/${entityId}`);
  }
}

export interface PartialEntity {
  name: string;
  acronym: string;
  entityTypeId: number;
  entity_profile: EntityProfile;
  user_id: number | null | undefined;
}

export function fromPartialEntity(partial: PartialEntity): PartialEntityDTO {
  return {
    name: partial.name,
    acronym: partial.acronym,
    entity_type_id: partial.entityTypeId,
    entity_profile: partial.entity_profile,
    user_id: partial.user_id,
  };
}

export type DepartmentCreate = Omit<Department, "id">;

export interface PartialEntityDTO {
  name: string;
  acronym: string;
  entity_type_id: number;
  entity_profile: EntityProfile;
  user_id: number | null | undefined;
}

export function fromPartialEntityDTO(dto: PartialEntityDTO): PartialEntity {
  return {
    name: dto.name,
    acronym: dto.acronym,
    entityTypeId: dto.entity_type_id,
    entity_profile: dto.entity_profile,
    user_id: dto.user_id,
  };
}

export interface EntityDTO {
  id: number;
  name: string;
  acronym: string;
  entity_type_id: number;
  created_at: string;
  entity_profile: EntityProfile;
  user_id: number | null | undefined;
  main_user?: User;
  entity_score?: number;
  is_active: number;
}

export function fromEntityDTO(dto: EntityDTO): Entity {
  return {
    name: dto.name,
    acronym: dto.acronym,
    id: dto.id,
    entityTypeId: dto.entity_type_id,
    createdAt: new Date(Date.parse(dto.created_at)),
    entity_profile: dto.entity_profile,
    user_id: dto.user_id,
    main_user: dto.main_user,
    entity_score: dto.entity_score,
    is_active: dto.is_active,
  };
}
export interface SearchEntitiesParams {
  ids?: number[];
  name?: string;
  acronym?: string;
  type_ids?: number[];
  name_or_acronym?: string;
  is_main_page?: boolean;
}

export type DepartmentDTO = SnakeCasedProperties<Department>;

export function fromDepartmentDTO(dto: DepartmentDTO): Department {
  return {
    id: dto.id,
    name: dto.name,
    acronym: dto.acronym,
    entityId: dto.entity_id,
    index: dto.index,
    status: dto.status,
  };
}
